import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { RoutePermissionDenied } from '@/components/molecules';
import { Skeleton } from '@/components/atoms/skeleton';
import { LeadFormContent } from '@/components/organisms/leads/LeadFormSheet';
import { useLead } from '@/hooks/leads/useLead';
import { useRoutePermission } from '@/hooks/navigation/useRoutePermission';

export const Route = createFileRoute('/_dashboard/dashboard/leads/$leadId/edit')({
  component: EditLeadPage,
});

function EditLeadPage() {
  const { leadId } = Route.useParams();
  const search = Route.useSearch();
  const router = useRouter();
  const canEditLeads = useRoutePermission('leads:edit');
  const leadQuery = useLead(leadId, { enabled: canEditLeads });
  const lead = leadQuery.data;

  function closeToDetail() {
    void router.navigate({
      to: '/dashboard/leads/$leadId',
      params: { leadId },
      search: { ...search, tab: 'overview' },
    });
  }

  if (!canEditLeads) {
    return (
      <RoutePermissionDenied
        title="Lead editing unavailable"
        message="You do not have permission to edit leads."
        className="py-24"
        action={<Button onClick={closeToDetail}>Back to Lead</Button>}
      />
    );
  }

  if (leadQuery.isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (leadQuery.isError || !lead) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div>
          <p className="text-lg font-semibold">Lead not found</p>
          <p className="text-sm text-muted-foreground">
            This lead doesn't exist or has been deleted.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void leadQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
          <Button onClick={closeToDetail}>Back to Lead</Button>
        </div>
      </div>
    );
  }

  if (lead.isClosed) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div>
          <p className="text-lg font-semibold">Lead is closed</p>
          <p className="text-sm text-muted-foreground">
            Closed leads cannot be edited. Create a new lead for a new opportunity.
          </p>
        </div>
        <Button onClick={closeToDetail}>Back to Lead</Button>
      </div>
    );
  }

  return (
    <LeadFormContent
      presentation="page"
      lead={lead}
      onClose={closeToDetail}
      onSuccess={closeToDetail}
    />
  );
}
